import React from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Button} from 'antd';
import {PlusOutlined, EllipsisOutlined, DeleteOutlined} from '@ant-design/icons';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {SortableTask} from './SortableTask';

export const SortableList = ({list, onAddTask, onEditList, onDeleteList}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: `list-${list.id}`,
        data: {
            type: 'list',
            list,
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="w-80 flex-shrink-0 h-[calc(100vh-25rem)]"
        >
            <div
                className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md border border-white/20 h-full flex flex-col transition-all duration-300 hover:shadow-lg">
                {/* List Header */}
                <div
                    className="p-4 border-b border-gray-100 flex justify-between items-center group cursor-grab active:cursor-grabbing"
                    {...attributes}
                    {...listeners}
                >
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-800 m-0">
                            {list.name}
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {list.tasks?.length || 0}
                        </span>
                    </div>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                            type="text"
                            size="small"
                            icon={<EllipsisOutlined/>}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditList(list);
                            }}
                            className="hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center"
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined/>}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteList();
                            }}
                            className="hover:bg-red-50 hover:text-red-500 rounded-full w-8 h-8 flex items-center justify-center"
                        />
                    </div>
                </div>

                {/* Tasks Container */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                    <SortableContext
                        items={list.tasks?.map(task => `task-${task.id}`) || []}
                        strategy={verticalListSortingStrategy}
                    >
                        {list.tasks?.map((task) => (
                            <SortableTask
                                key={task.id}
                                task={task}
                                listId={list.id.toString()}
                            />
                        ))}
                    </SortableContext>
                </div>

                {/* Add Task Button */}
                <div className="p-3 border-t border-gray-100">
                    <Button
                        type="text"
                        block
                        icon={<PlusOutlined/>}
                        onClick={onAddTask}
                        className="h-10 bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-200 hover:border-blue-400 text-gray-600 hover:text-blue-600 rounded-lg transition-all duration-300"
                    >
                        Add Task
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SortableList;

